import { Code, ContentCopy, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Button from 'material/components/Button';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setSnackbarError } from 'store/actions/global';
import { useCopyToClipboard } from 'utils/hooks';
import { StyledCode } from 'globalStyles';
import WidgetDialog from 'routes/ActionCenterDetails/components/WidgetDialog';
import {
  ActionButtonsWrapper,
  ActionCenterWidgetCheckboxWrapper,
  InputsRow,
  InputWrapper,
  OptionsSection,
  OptionsToggle,
  PreviewWrapper,
  StyledCard,
  StyledFormControl,
} from './styles';

/**
 * Embed-code generator card for the action center widget: pick an action
 * center, optionally tune width/height/full-height/custom CSS, then copy the
 * generated `<div>` + script snippet or view it in a dialog. Use wherever
 * users need the embed code for a version-4 action center.
 */
const ActionCenterWidget = ({
  defaultActionCenterId = '',
  reportsSchema = null,
  showActionCenterSelect = true,
}) => {
  const dispatch = useDispatch();
  const [copy] = useCopyToClipboard();
  const [selectedActionCenter, setSelectedActionCenter] = useState(defaultActionCenterId);
  const [showOptions, setShowOptions] = useState(false);
  const [width, setWidth] = useState('100');
  const [height, setHeight] = useState('1000');
  const [fullHeight, setFullHeight] = useState(false);
  const [customCssUrl, setCustomCssUrl] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleOptionsToggle = () => setShowOptions(prevState => !prevState);

  const getWidgetCode = () => {
    const { REACT_APP_LANDING_PAGE_URL, REACT_APP_WIDGET_V4_URL } = process.env;

    return `
    <div
      class="muster-embed muster-embed-action"
      data-action-center-id="${selectedActionCenter}"
      data-domain="${REACT_APP_LANDING_PAGE_URL}"
      data-height="${height}"
      data-width="${width}%"
      data-height-full="${fullHeight ? 'true' : 'false'}"
      data-css="${customCssUrl}"
      id="muster-action-embed-${selectedActionCenter}"
    >
      <script
        src="${REACT_APP_WIDGET_V4_URL}"
        widget-id="muster-action-embed-${selectedActionCenter}"
      ></script>
    </div>
  `;
  };

  const validate = () => {
    if (!selectedActionCenter) {
      dispatch(setSnackbarError({ message: 'Please select an action center' }));

      return false;
    }

    if (customCssUrl && (!customCssUrl.startsWith('https') || !customCssUrl.endsWith('.css'))) {
      dispatch(
        setSnackbarError({ message: 'Custom CSS URL must start with https and end with .css' })
      );

      return false;
    }

    return true;
  };

  const handleCopyToClipboard = () => {
    if (!validate()) return;

    copy(getWidgetCode(), 'textarea', 'Code');
  };

  const handleViewCode = () => {
    if (!validate()) return;

    setIsDialogOpen(true);
  };

  const handleDialogCopyToClipboard = () => {
    copy(getWidgetCode(), 'textarea', 'Code');
    setIsDialogOpen(false);
  };

  return (
    <>
      <StyledCard>
        <PreviewWrapper>
          <img
            alt="Action center widget preview"
            height="200px"
            src="/images/widget-preview-elegante.webp"
          />
        </PreviewWrapper>

        {showActionCenterSelect && (
          <StyledFormControl variant="outlined">
            <InputLabel htmlFor="action-center-widget-select" sx={{ backgroundColor: 'white' }}>
              Action Center
            </InputLabel>
            <Select
              variant="outlined"
              native
              value={selectedActionCenter}
              onChange={e => setSelectedActionCenter(e.target.value)}
              label="Action Center"
              inputProps={{
                id: 'action-center-widget-select',
                name: 'actionCenter',
              }}
            >
              <option aria-label="None" value="" />
              {reportsSchema?.campaigns?.map(campaign => (
                <optgroup key={campaign.uuid} label={campaign.name}>
                  {campaign.actionCenters
                    ?.filter(ac => !ac.version || ac.version === 'version4')
                    .map(ac => (
                      <option key={ac.uuid} value={ac.uuid}>
                        {ac.name}
                      </option>
                    ))}
                </optgroup>
              ))}
            </Select>
          </StyledFormControl>
        )}

        <OptionsToggle onClick={handleOptionsToggle}>
          {showOptions ? 'Hide options' : 'Options'}
          {showOptions ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </OptionsToggle>

        {showOptions && (
          <OptionsSection>
            <InputsRow>
              <InputWrapper>
                <TextField
                  fullWidth
                  label="Width (%)"
                  value={width}
                  onChange={e => setWidth(e.target.value)}
                  variant="outlined"
                  size="medium"
                  helperText="Populates `data-width`"
                />
              </InputWrapper>
              <InputWrapper>
                <TextField
                  fullWidth
                  label="Height (pixels)"
                  value={height}
                  onChange={e => setHeight(e.target.value)}
                  variant="outlined"
                  size="medium"
                  helperText="Populates `data-height`"
                />
              </InputWrapper>
            </InputsRow>

            <ActionCenterWidgetCheckboxWrapper>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fullHeight}
                    onChange={e => setFullHeight(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={<Typography variant="body2">Full height</Typography>}
              />
              <FormHelperText
                sx={{
                  marginLeft: '14px',
                  marginRight: '14px',
                  marginTop: '3px',
                }}
              >
                Populates `data-height-full`
              </FormHelperText>
            </ActionCenterWidgetCheckboxWrapper>

            <InputWrapper>
              <TextField
                fullWidth
                label="Custom CSS (Optional)"
                value={customCssUrl}
                onChange={e => setCustomCssUrl(e.target.value)}
                variant="outlined"
                size="medium"
                placeholder="https://example.com/styles.css"
                helperText="Populates `data-css`"
              />
            </InputWrapper>
          </OptionsSection>
        )}

        <ActionButtonsWrapper>
          <Button variant="text" startIcon={<ContentCopy />} onClick={handleCopyToClipboard}>
            Copy to Clipboard
          </Button>
          <Button variant="contained" startIcon={<Code />} onClick={handleViewCode}>
            View Code
          </Button>
        </ActionButtonsWrapper>
      </StyledCard>
      <WidgetDialog
        isDialogOpen={isDialogOpen}
        handleDialogClose={() => setIsDialogOpen(false)}
        title="Widget Code"
        handleCopyToClipboard={handleDialogCopyToClipboard}
      >
        <StyledCode>{getWidgetCode()}</StyledCode>
      </WidgetDialog>
    </>
  );
};

ActionCenterWidget.propTypes = {
  /** Preselected action center UUID; pair with `showActionCenterSelect={false}` on detail pages. */
  defaultActionCenterId: PropTypes.string,
  /** Campaign → action-center tree that populates the selector; only version4 (or unversioned) action centers are listed. */
  reportsSchema: PropTypes.shape({
    campaigns: PropTypes.arrayOf(
      PropTypes.shape({
        actionCenters: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            uuid: PropTypes.string,
            version: PropTypes.string,
          })
        ),
        name: PropTypes.string,
        uuid: PropTypes.string,
      })
    ),
  }),
  showActionCenterSelect: PropTypes.bool,
};

export default ActionCenterWidget;
